/**
 * Écrans moniteur (§ 15.2) : calendrier avec panneau d'administration de
 * séance (annuler, déplacer, présence, athlètes), séance ponctuelle, ajout
 * d'athlète, mes présences (nombre de séances, jamais de montant).
 */
import React, { useState } from 'react';
import { api, uuid } from '../lib/api.js';
import { Spinner, ErrorBox, Badge, useApi, UserSearch, Modal, Intro, IconeLieu, peut, groupesDe } from '../components/common.jsx';
import { fmtDate, capitaliser } from '../lib/format.js';

/** Mes entraînements (moniteur) : liste avec vote Présent / Absent et
 *  annulation confirmée par une fenêtre de dialogue. */
export function MoniteurCalendrier({ me, toast, lieux }) {
  const [state, reload] = useApi('moniteur/entrainements', { cache: 'moniteur_entrainements' });
  const [creer, setCreer] = useState(false);
  const groupesMoniteur = groupesDe(me).filter((g) => g.role === 'moniteur');

  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;

  async function voter(s, etat) {
    const r = await api(`seances/${s.id}/presence`, {
      method: 'POST',
      body: { type: s.phase, etat },
      queueable: true,
      idempotencyKey: uuid(),
      label: `Présence — ${s.groupe} ${s.date}`,
    });
    if (r.ok && r.queued) toast('Hors ligne : votre réponse sera transmise à la reconnexion.');
    else if (r.ok) { toast(etat === 'present' ? 'Présence enregistrée.' : 'Absence enregistrée.'); reload(); }
    else toast(r.error, true);
  }

  async function annuler(s) {
    // Confirmation obligatoire pour éviter une annulation par erreur.
    if (!confirm(`Annuler l'entraînement « ${s.groupe} » du ${fmtDate(s.date)} ?\n\nLes membres du groupe verront la séance comme annulée et seront notifiés.`)) return;
    const motif = prompt('Motif de l\'annulation (communiqué au groupe) :');
    if (motif === null) return;
    const r = await api(`seances/${s.id}/annuler`, {
      method: 'POST', body: { motif: motif || 'Non précisé' },
      idempotencyKey: uuid(),
    });
    if (r.ok) { toast('Entraînement annulé — le groupe est notifié.'); reload(); }
    else toast(r.error, true);
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <h2 className="h4 mb-0">Gestion des entraînements</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setCreer(true)}>+ Entraînement ponctuel</button>
      </div>
      <Intro>Vos prochaines séances en tant que moniteur : annoncez votre présence,
        créez un entraînement ponctuel ou annulez une séance.</Intro>
      {groupesMoniteur.length > 0 && (
        <div className="mb-3">
          <h6 className="mb-2">Mes groupes</h6>
          <div className="d-flex gap-2 flex-wrap">
            {groupesMoniteur.map((g) => (
              <button key={g.id} type="button" className="btn btn-outline-primary btn-sm"
                onClick={() => { location.hash = '#/moniteur-groupe/' + g.id; }}>
                {g.nom} — participants
              </button>
            ))}
          </div>
        </div>
      )}
      {state.data.length === 0 && <p className="text-muted">Aucun entraînement sur la période.</p>}
      <div className="list-group">
        {state.data.map((s) => (
          <div key={s.id} className="list-group-item py-3">
            <div className={s.statut === 'annulee' ? 'text-decoration-line-through text-muted' : ''}>
              <strong>{capitaliser(fmtDate(s.date))}</strong> · {s.heure_debut}–{s.heure_fin}
              <br /><small className="text-muted">{s.groupe}{s.ponctuelle ? ' · ★ ponctuel' : ''}</small>
              {s.lieu && (
                <><br /><small className="text-muted d-inline-flex align-items-center gap-1">
                  <IconeLieu taille={12} /> {s.lieu}
                </small></>
              )}
            </div>
            {s.statut === 'annulee' ? (
              <span className="badge bg-danger mt-2">Annulé{s.motif ? ' — ' + s.motif : ''}</span>
            ) : s.statut === 'vacances' ? (
              <span className="badge bg-secondary mt-2">Vacances</span>
            ) : (
              // Trois colonnes égales : les trois actions ont le même poids et
              // restent atteignables au pouce sur mobile.
              <div className="row g-2 mt-1">
                <div className="col-4 d-grid">
                  <button type="button"
                    className={`btn btn-sm ${s.mon_vote === 'present' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => voter(s, 'present')}>
                    Présent{s.mon_vote === 'present' ? ' ✓' : ''}
                  </button>
                </div>
                <div className="col-4 d-grid">
                  <button type="button"
                    className={`btn btn-sm ${s.mon_vote === 'absent' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => voter(s, 'absent')}>
                    Absent{s.mon_vote === 'absent' ? ' ✓' : ''}
                  </button>
                </div>
                <div className="col-4 d-grid">
                  <button type="button" className="btn btn-sm btn-warning" onClick={() => annuler(s)}>
                    Annulation
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="text-muted small mt-2">Présent / Absent restent modifiables (prévisionnel jusqu'au début de la séance, effectif jusqu'à la clôture de la période).</p>
      {creer && <SeancePonctuelle groupes={groupesMoniteur} lieux={lieux} toast={toast} onClose={() => { setCreer(false); reload(); }} />}
    </div>
  );
}

/** Panneau d'administration d'une séance : une action en un geste (§ 15.2). */
export function PanneauSeance({ seanceId, me, toast, lieux, onClose }) {
  const [state, reload] = useApi('seances/' + seanceId, { deps: [seanceId] });
  const [presences] = useApi('seances/' + seanceId + '/presences', { deps: [seanceId] });
  const [athletes, setAthletes] = useState(null);
  const [action, setAction] = useState(null); // 'annuler' | 'deplacer'
  if (state.loading) return <Modal title="Séance" onClose={onClose}><Spinner /></Modal>;
  if (!state.ok) return <Modal title="Séance" onClose={onClose}><ErrorBox message={state.error} /></Modal>;
  const s = state.data;
  const estMoniteurDuGroupe = groupesDe(me).some((g) => g.id === s.groupe_id && g.role === 'moniteur') || peut(me, 'adlt_manage_groupes');

  async function chargerAthletes() {
    const r = await api(`groupes/${s.groupe_id}/membres`);
    setAthletes(r.ok ? r.data.filter((m) => m.role === 'athlete') : []);
  }

  async function presence(type, etat) {
    const r = await api(`seances/${seanceId}/presence`, {
      method: 'POST', body: { type, etat },
      queueable: true, idempotencyKey: uuid(),
      label: `Présence ${type} — ${s.groupe_nom} ${s.date}`,
    });
    if (r.ok && r.queued) toast('Hors ligne : votre présence sera transmise à la reconnexion.');
    else if (r.ok) toast('Présence enregistrée.');
    else toast(r.error, true);
  }

  async function annuler(e) {
    e.preventDefault();
    const motif = e.target.motif.value;
    const r = await api(`seances/${seanceId}/annuler`, {
      method: 'POST', body: { motif },
      queueable: true, idempotencyKey: uuid(),
      label: `Annulation — ${s.groupe_nom} ${s.date}`,
    });
    if (r.ok && r.queued) toast('Hors ligne : l\'annulation sera transmise à la reconnexion.');
    else if (r.ok) toast('Séance annulée, le groupe est notifié.');
    else toast(r.error, true);
    if (r.ok) { setAction(null); reload(); }
  }

  async function deplacer(e) {
    e.preventDefault();
    const f = e.target;
    const r = await api(`seances/${seanceId}`, {
      method: 'PATCH',
      body: { date: f.date.value, heure_debut: f.heure_debut.value, heure_fin: f.heure_fin.value, lieu_id: f.lieu_id.value, motif: f.motif.value },
    });
    if (r.ok) { toast('Séance déplacée, le groupe est notifié.'); setAction(null); reload(); }
    else toast(r.error, true);
  }

  const ma = (presences.ok ? presences.data : []).find((p) => p.user_id === me.id);

  return (
    <Modal title={`${s.groupe_nom} — ${fmtDate(s.date)}`} onClose={onClose}>
      <p className="text-muted mb-2">
        {s.heure_debut}–{s.heure_fin} · {s.lieu_nom} <Badge statut={s.statut} />
        {s.ponctuelle && <span className="badge bg-primary ms-1">★ ponctuel</span>}
        {s.motif && <><br />Motif : {s.motif}</>}
      </p>

      {estMoniteurDuGroupe && s.statut === 'planifiee' && (
        <>
          <h6 className="mt-3">Ma présence</h6>
          <div className="d-flex gap-2 flex-wrap mb-2">
            <button className={`btn btn-sm ${ma?.etat_previsionnel === 'present' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => presence('previsionnel', 'present')}>Prévue ✓</button>
            <button className={`btn btn-sm ${ma?.etat_previsionnel === 'absent' ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => presence('previsionnel', 'absent')}>Prévue ✗</button>
            <button className={`btn btn-sm ${ma?.etat_effectif === 'present' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => presence('effectif', 'present')}>Donnée ✓</button>
            <button className={`btn btn-sm ${ma?.etat_effectif === 'absent' ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => presence('effectif', 'absent')}>Donnée ✗</button>
          </div>

          <h6 className="mt-3">Actions sur la séance</h6>
          <div className="d-flex gap-2 flex-wrap mb-2">
            <button className="btn btn-outline-danger btn-sm" onClick={() => setAction(action === 'annuler' ? null : 'annuler')}>Annuler…</button>
            <button className="btn btn-outline-warning btn-sm" onClick={() => setAction(action === 'deplacer' ? null : 'deplacer')}>Déplacer…</button>
            <button className="btn btn-outline-primary btn-sm" onClick={chargerAthletes}>Athlètes du groupe</button>
          </div>
          {action === 'annuler' && (
            <form onSubmit={annuler} className="border rounded p-3 mb-2">
              <label className="form-label">Motif de l'annulation *</label>
              <input name="motif" className="form-control mb-2" required />
              <p className="text-muted small">Les athlètes et moniteurs du groupe seront notifiés par courriel et push.</p>
              <button className="btn btn-danger btn-sm">Confirmer l'annulation</button>
            </form>
          )}
          {action === 'deplacer' && (
            <form onSubmit={deplacer} className="border rounded p-3 mb-2">
              <div className="row g-2">
                <div className="col-6"><label className="form-label">Nouvelle date *</label><input name="date" type="date" className="form-control" defaultValue={s.date} required /></div>
                <div className="col-3"><label className="form-label">Début</label><input name="heure_debut" type="time" className="form-control" defaultValue={s.heure_debut} /></div>
                <div className="col-3"><label className="form-label">Fin</label><input name="heure_fin" type="time" className="form-control" defaultValue={s.heure_fin} /></div>
                <div className="col-8"><label className="form-label">Lieu</label>
                  <select name="lieu_id" className="form-select" defaultValue={s.lieu_id}>{(lieux || []).map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}</select></div>
                <div className="col-12"><label className="form-label">Motif *</label><input name="motif" className="form-control" required /></div>
              </div>
              <button className="btn btn-warning btn-sm mt-2">Confirmer le déplacement</button>
            </form>
          )}
        </>
      )}

      {presences.ok && presences.data.length > 0 && (
        <>
          <h6 className="mt-3">Moniteurs</h6>
          <ul className="list-unstyled mb-2">
            {presences.data.map((p) => (
              <li key={p.id}>
                {p.nom} <small className="text-muted">({p.role === 'moniteur_principal' ? 'principal' : 'assistant'})</small>
                {' '}{p.etat_previsionnel && <span className="badge bg-light text-dark border">prévu : {p.etat_previsionnel}</span>}
                {' '}{p.etat_effectif && <span className="badge bg-light text-dark border">effectif : {p.etat_effectif}</span>}
                {' '}{p.validee && <span className="badge bg-success">validée</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      {athletes && (
        <>
          <h6 className="mt-3">Athlètes du groupe ({athletes.length})</h6>
          <ul className="list-unstyled">{athletes.map((m) => <li key={m.user_id}>{m.nom}</li>)}</ul>
        </>
      )}
    </Modal>
  );
}

/** Création d'une séance ponctuelle (autorisée même en vacances, § 4.3). */
export function SeancePonctuelle({ groupes, lieux, toast, onClose }) {
  async function submit(e) {
    e.preventDefault();
    const f = e.target;
    const r = await api('seances', {
      method: 'POST',
      body: { groupe_id: f.groupe_id.value, date: f.date.value, heure_debut: f.heure_debut.value, heure_fin: f.heure_fin.value, lieu_id: f.lieu_id.value },
      idempotencyKey: uuid(),
    });
    if (r.ok) { toast('Entraînement ponctuel créé.'); onClose(); }
    else toast(r.error, true);
  }
  return (
    <Modal title="Nouvel entraînement ponctuel" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="row g-2">
          <div className="col-12"><label className="form-label">Groupe *</label>
            <select name="groupe_id" className="form-select" required>{groupes.map((g) => <option key={g.id} value={g.id}>{g.nom}</option>)}</select></div>
          <div className="col-6"><label className="form-label">Date *</label><input name="date" type="date" className="form-control" required /></div>
          <div className="col-3"><label className="form-label">Début *</label><input name="heure_debut" type="time" className="form-control" required /></div>
          <div className="col-3"><label className="form-label">Fin *</label><input name="heure_fin" type="time" className="form-control" required /></div>
          <div className="col-12"><label className="form-label">Lieu *</label>
            <select name="lieu_id" className="form-select" required>{(lieux || []).map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}</select></div>
        </div>
        <p className="text-muted small mt-2">Autorisée même pendant une période de vacances sans salle.</p>
        <button className="btn btn-primary mt-2">Créer la séance</button>
      </form>
    </Modal>
  );
}

/** Gestion d'un groupe : membres + ajout d'athlète par recherche (§ 15.2). */
export function MoniteurGroupe({ groupeId, me, toast, onBack }) {
  const [state, reload] = useApi(`groupes/${groupeId}/membres`, { deps: [groupeId] });
  const [groupe] = useApi(`groupes/${groupeId}`, { deps: [groupeId] });
  const [role, setRole] = useState('athlete');
  const comite = peut(me, 'adlt_manage_groupes');
  if (state.loading || groupe.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;

  async function ajouter(u) {
    const r = await api(`groupes/${groupeId}/membres`, {
      method: 'POST', body: { user_id: u.id, role },
      queueable: role === 'athlete', idempotencyKey: uuid(),
      label: `Ajout de ${u.nom} au groupe`,
    });
    if (r.ok && r.queued) toast('Hors ligne : l\'ajout sera transmis à la reconnexion.');
    else if (r.ok) { toast(`${u.nom} ajouté(e) au groupe (${role === 'athlete' ? 'athlète' : role === 'moniteur_principal' ? 'moniteur principal' : 'moniteur assistant'}).`); reload(); }
    else toast(r.error, true);
  }

  async function retirer(uid, nom) {
    if (!confirm(`Terminer l'affectation de ${nom} ?`)) return;
    const r = await api(`groupes/${groupeId}/membres/${uid}`, { method: 'DELETE' });
    if (r.ok) { toast('Affectation terminée (historique conservé).'); reload(); }
    else toast(r.error, true);
  }

  const moniteurs = state.data.filter((m) => m.role !== 'athlete');
  const athletes = state.data.filter((m) => m.role === 'athlete');

  return (
    <div>
      {onBack && <button className="btn btn-link px-0" onClick={onBack}>‹ Retour</button>}
      <h2 className="h4 mb-3">{groupe.ok ? groupe.data.nom : 'Groupe'}</h2>
      <div className="card mb-3"><div className="card-body">
        <h6>Ajouter {comite ? 'un membre' : 'un athlète'}</h6>
        <div className="d-flex gap-2 flex-wrap">
          {comite && (
            <select className="form-select" style={{ maxWidth: 220 }} value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="athlete">Athlète</option>
              <option value="moniteur_principal">Moniteur principal</option>
              <option value="moniteur_assistant">Moniteur assistant</option>
            </select>
          )}
          <div style={{ maxWidth: 380, flexGrow: 1 }}><UserSearch onSelect={ajouter} /></div>
        </div>
        {comite && <small className="text-muted">Plusieurs moniteurs principaux (responsables) et assistants possibles par groupe.</small>}
      </div></div>
      <h6>Moniteurs</h6>
      <ul className="list-group mb-3">
        {moniteurs.map((m) => (
          <li key={m.user_id} className="list-group-item d-flex justify-content-between">
            <span>{m.nom} <small className="text-muted">({m.role === 'moniteur_principal' ? 'principal' : 'assistant'})</small></span>
          </li>
        ))}
      </ul>
      <h6>Athlètes ({athletes.length})</h6>
      <ul className="list-group">
        {athletes.map((m) => (
          <li key={m.user_id} className="list-group-item d-flex justify-content-between align-items-center">
            <span>{m.nom} <small className="text-muted">depuis le {m.depuis}</small></span>
            <button className="btn btn-outline-danger btn-sm" onClick={() => retirer(m.user_id, m.nom)}>Retirer</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Mes présences : nombre de séances validées — aucun montant (§ 10.2).
 *  Même mise en page en liste que « Mes entraînements ». */
export function MoniteurPresences({ me }) {
  const annee = new Date().getFullYear();
  const [state] = useApi(`remuneration/mes-seances?du=${annee}-01-01&au=${annee}-12-31`, { cache: 'mes_seances' });
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const d = state.data;
  const ETATS = {
    present: <span className="badge bg-success">Présent</span>,
    absent: <span className="badge bg-danger">Absent</span>,
  };
  return (
    <div>
      <h2 className="h4 mb-2">Décomptes des présences {annee}</h2>
      <Intro>Le décompte de vos séances données et validées pour l'année en cours.
        La validation relève du responsable des entraînements.</Intro>
      <div className="alert alert-primary">
        <strong>{d.nombre_validees}</strong> séance(s) donnée(s) et validée(s) cette année.
      </div>
      {d.seances.length === 0 && <p className="text-muted">Aucune séance sur la période.</p>}
      <div className="list-group">
        {d.seances.map((s, i) => (
          <div key={i} className="list-group-item py-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span>
                <strong>{fmtDate(s.date)}</strong> · {String(s.heure_debut).slice(0, 5)}–{String(s.heure_fin).slice(0, 5)}
                <br /><small className="text-muted">{s.groupe_nom} · {s.role === 'moniteur_principal' ? 'principal' : 'assistant'}</small>
              </span>
              <span className="d-flex gap-2 flex-wrap align-items-center">
                <small className="text-muted">Prévue :</small>{ETATS[s.etat_previsionnel] || <span className="badge bg-secondary">—</span>}
                <small className="text-muted">Donnée :</small>{ETATS[s.etat_effectif] || <span className="badge bg-secondary">—</span>}
                {s.validee ? <span className="badge bg-success">Validée ✓</span> : <span className="badge bg-secondary">À valider</span>}
              </span>
            </div>
          </div>
        ))}
      </div>
      <p className="text-muted small mt-2">La validation des présences relève du responsable des entraînements. Le décompte fait foi après validation.</p>
    </div>
  );
}
